import { lazyPage, type ModulePlugin } from "@keel/core";

const invoices = () => import("./Invoices");
const ledgers = () => import("./Ledgers");
const InvoicesList = lazyPage(invoices, "InvoicesList");
const InvoicePage = lazyPage(invoices, "InvoicePage");
const Payments = lazyPage(() => import("./Payments"), "Payments");
const PartyLedger = lazyPage(ledgers, "PartyLedger");
const LedgerEntriesTab = lazyPage(ledgers, "LedgerEntriesTab");
const ChasingTab = lazyPage(() => import("./Chasing"), "ChasingTab");
const GlEntriesTab = lazyPage(ledgers, "GlEntriesTab");
const PartyInvoicesTab = lazyPage(ledgers, "PartyInvoicesTab");
const PartyStats = lazyPage(ledgers, "PartyStats");
const PartyBalanceCell = lazyPage(ledgers, "PartyBalanceCell");
const StatementButton = lazyPage(ledgers, "StatementButton");
const Vat = lazyPage(() => import("./Vat"), "Vat");
const FxRevaluation = lazyPage(() => import("./FxRevaluation"), "FxRevaluation");
const BankAccountsTab = lazyPage(() => import("./BankAccounts"), "BankAccountsTab");
const NewInvoiceButton = lazyPage(() => import("./Slots"), "NewInvoiceButton");
const statementsPage = () => import("./Statements");
const Statements = lazyPage(statementsPage, "Statements");
const StatementPage = lazyPage(statementsPage, "StatementPage");
const runsPage = () => import("./PaymentRuns");
const PaymentRuns = lazyPage(runsPage, "PaymentRuns");
const PaymentRunPage = lazyPage(runsPage, "PaymentRunPage");
const Reminders = lazyPage(() => import("./Reminders"), "Reminders");
const DunningLevelsTab = lazyPage(() => import("./DunningLevels"), "DunningLevelsTab");

/**
 * Invoicing & receivables: invoices, bills, payments, sales and purchase ledgers, and the ledger
 * parts of customer and supplier cards. Ported from the prototype's `pages/finance`.
 */
const plugin: ModulePlugin = {
  key: "finance",
  records: { Invoice: "/invoices/", Bill: "/bills/", CreditNote: "/invoices/" },
  help: () => import("./help"),
  nav: [
    {
      group: "Sales",
      items: [{ to: "/invoices", label: "Invoices", icon: "invoice", perm: "finance.invoices.read" }],
    },
    {
      group: "Purchasing",
      items: [{ to: "/bills", label: "Bills", icon: "receipt", perm: "finance.invoices.read" }],
    },
    {
      // Its own section, not a tail on Accounting: the subledgers and the money moving in and out
      // are a different day's work from the general ledger and the statutory reports.
      group: "Finance",
      items: [
        { to: "/sales-ledger", label: "Sales ledger", icon: "users", perm: "finance.invoices.read" },
        { to: "/purchase-ledger", label: "Purchase ledger", icon: "factory", perm: "finance.invoices.read" },
        { to: "/vat", label: "VAT", icon: "receipt", perm: "finance.invoices.read" },
        { to: "/payments", label: "Payments", icon: "cash", perm: "finance.payments.read" },
        {
          to: "/fx-revaluation",
          label: "Exchange revaluation",
          icon: "refresh",
          perm: "finance.invoices.read",
        },
        {
          to: "/bank-statements",
          label: "Bank statements",
          icon: "wallet",
          perm: "finance.statements.read",
        },
        {
          to: "/payment-runs",
          label: "Payment runs",
          icon: "send",
          perm: "finance.payments.read",
        },
        {
          to: "/reminders",
          label: "Payment reminders",
          icon: "alert",
          perm: "finance.invoices.read",
        },
      ],
    },
  ],
  routes: [
    { path: "/invoices", element: <InvoicesList type="sales" /> },
    { path: "/invoices/:id", element: <InvoicePage type="sales" /> },
    { path: "/bills", element: <InvoicesList type="purchase" /> },
    { path: "/bills/:id", element: <InvoicePage type="purchase" /> },
    { path: "/payments", element: <Payments /> },
    { path: "/bank-statements", element: <Statements /> },
    { path: "/bank-statements/:id", element: <StatementPage /> },
    { path: "/payment-runs", element: <PaymentRuns /> },
    { path: "/payment-runs/:id", element: <PaymentRunPage /> },
    { path: "/sales-ledger", element: <PartyLedger type="sales" /> },
    { path: "/purchase-ledger", element: <PartyLedger type="purchase" /> },
    { path: "/vat", element: <Vat /> },
    { path: "/fx-revaluation", element: <FxRevaluation /> },
    { path: "/reminders", element: <Reminders /> },
  ],
  commands: [
    ["New invoice", "/invoices/new", "plus", "finance.invoices.write"],
    ["New bill", "/bills/new", "plus", "finance.invoices.write"],
    ["Invoices", "/invoices", "invoice", "finance.invoices.read"],
    ["Overdue invoices", "/invoices?tab=overdue", "alert", "finance.invoices.read"],
    ["Bills", "/bills", "receipt", "finance.invoices.read"],
    ["Payments", "/payments", "cash", "finance.payments.read"],
    ["Bank statements", "/bank-statements", "wallet", "finance.statements.read"],
    ["Pay suppliers", "/payment-runs", "send", "finance.payments.read"],
    ["Sales ledger", "/sales-ledger", "users", "finance.invoices.read"],
    ["Purchase ledger", "/purchase-ledger", "factory", "finance.invoices.read"],
    ["VAT return", "/vat", "receipt", "finance.invoices.read"],
    ["Exchange revaluation", "/fx-revaluation", "refresh", "finance.invoices.read"],
    ["Payment reminders", "/reminders", "alert", "finance.invoices.read"],
  ],
  newMenu: [
    { label: "Invoice", to: "/invoices/new", perm: "finance.invoices.write", key: "i", section: "Documents" },
    { label: "Bill", to: "/bills/new", perm: "finance.invoices.write", section: "Documents" },
  ],
  settingsTabs: [
    {
      key: "bank-accounts",
      label: "Bank accounts",
      perm: "finance.payments.read",
      element: <BankAccountsTab />,
    },
    {
      key: "reminders",
      label: "Payment reminders",
      perm: "finance.invoices.read",
      element: <DunningLevelsTab />,
    },
  ],
  sources: { invoice: "/invoices/" },
  partyActions: [
    { key: "invoice", perm: "finance.invoices.write", component: NewInvoiceButton },
    { key: "statement", perm: "finance.invoices.read", component: StatementButton },
  ],
  partyColumns: [
    {
      key: "balance",
      label: "Balance",
      perm: "finance.invoices.read",
      align: "right",
      component: PartyBalanceCell,
    },
  ],
  partyStats: [{ key: "ledger", perm: "finance.invoices.read", component: PartyStats }],
  partyTabs: [
    { key: "ledger", label: "Ledger entries", perm: "finance.invoices.read", component: LedgerEntriesTab },
    { key: "gl", label: "G/L entries", perm: "finance.invoices.read", component: GlEntriesTab },
    {
      key: "invoices",
      label: "Invoices & bills",
      perm: "finance.invoices.read",
      component: PartyInvoicesTab,
    },
    {
      key: "chasing",
      label: "Reminders & interest",
      perm: "finance.invoices.read",
      kinds: ["customers"],
      component: ChasingTab,
    },
  ],
};
export default plugin;
